import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  listenForAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
} from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Check, X } from "lucide-react";

/* ─── Asset imports ─────────────────────────────────────── */
import logoImg from "../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   3D BEVEL TOKENS
   Same "toy button" logic as the rest of the app: every
   surface gets a thin white ring, a colored border, and a
   solid drop-step shadow underneath it so nothing looks flat.
   The card's own accent (blue) is reserved for the one button
   that actually gets you in — same rule as gold-for-the-door
   elsewhere, just recolored to this screen's palette.
═══════════════════════════════════════════════════════════ */
const G = {
  blue: "#4285F4",
  blueLit: "#6EA1FF",
  blueDeep: "#2A56C6",

  red: "#EA4335",
  redDeep: "#B23023",

  yellow: "#FBBC05",
  yellowDeep: "#C79104",

  green: "#34A853",
  greenDeep: "#1E7E3E",

  white: "#FFFFFF",
  bg: "#F8F9FA",
  text: "#202124",
  soft: "#5F6368",
  faint: "#B7BFCB",
  border: "#E8EAED",
};

const RING = `0 0 0 2px ${G.white}, 0 0 0 4.5px`;
const bevel = (edgeColor: string, depth = 6) => `${RING} ${edgeColor}, 0 ${depth}px 0 ${edgeColor}`;

const FONT = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap');

@keyframes fadeUp{ from{opacity:0; transform:translateY(16px);} to{opacity:1; transform:none;} }
@keyframes orb{ 0%,100%{transform:translate(0,0) scale(1);} 33%{transform:translate(-20px,15px) scale(1.05);} 66%{transform:translate(15px,-10px) scale(.97);} }
@keyframes logoPop{ 0%{transform:scale(.35) rotate(-28deg); opacity:0;} 55%{transform:scale(1.1) rotate(7deg); opacity:1;} 75%{transform:scale(.95) rotate(-3deg);} 100%{transform:scale(1) rotate(0);} }
@keyframes floatY{ 0%,100%{transform:translateY(0);} 50%{transform:translateY(-7px);} }
@keyframes stackWiggle{ 0%,100%{transform:translate(var(--sx),var(--sy)) rotate(var(--sr));} 50%{transform:translate(var(--sx),calc(var(--sy) - 3px)) rotate(calc(var(--sr) * -1));} }
@keyframes shine{ 0%{transform:translateX(-130%) rotate(18deg); opacity:0;} 8%{opacity:.9;} 35%{opacity:0;} 100%{transform:translateX(160%) rotate(18deg); opacity:0;} }

.auth-card{ animation:fadeUp .45s cubic-bezier(.2,.8,.25,1) both; }
.eb-btn{ transition:transform .16s cubic-bezier(.2,.9,.3,1.4), box-shadow .16s; }
.eb-btn:hover:not(:disabled){ transform:translateY(-3px); }
.eb-btn:active:not(:disabled){ transform:translateY(3px) !important; }
.eb-field{ transition:box-shadow .16s; }
`;

/* ─── Password validation ───────────────────────────────── */
const validatePassword = (pwd: string, email: string) => {
  const username = email.split("@")[0]?.toLowerCase() || "";
  return {
    hasUpper: /[A-Z]/.test(pwd),
    hasLower: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
    validLength: pwd.length >= 6 && pwd.length <= 12,
    notEmail: pwd.toLowerCase() !== email.toLowerCase(),
    notContainEmail: !pwd.toLowerCase().includes(email.toLowerCase()) && !pwd.toLowerCase().includes(username),
  };
};

/* ─── Field component — pill input, inset bevel ring ────── */
const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  type?: string; error?: boolean;
  right?: React.ReactNode;
}> = ({ label, value, onChange, type = "text", error, right }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 800,
        color: G.soft, letterSpacing: "0.09em",
        marginBottom: 7, marginLeft: 4,
        fontFamily: "'Baloo 2', sans-serif", textTransform: "uppercase",
      }}>
        {label}
      </label>
      <div
        className="eb-field"
        style={{
          position: "relative", display: "flex", alignItems: "center",
          padding: "13px 44px 13px 16px", borderRadius: 18,
          background: G.bg,
          boxShadow: `inset 0 0 0 2.5px ${error ? G.red : focused ? G.blue : G.border}`,
        }}
      >
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none",
            background: "transparent", fontSize: 14.5, color: G.text,
            fontFamily: "'Nunito', sans-serif", fontWeight: 700,
          }}
        />
        {right && (
          <div style={{ position: "absolute", right: 14, display: "flex", alignItems: "center", color: G.soft, cursor: "pointer" }}>
            {right}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Logo badge — the only branding on the screen, no wordmark ── */
const LogoBadge: React.FC = () => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -20, y: px * 20 });
  };
  const reset = () => { setTilt({ x: 0, y: 0 }); setHovering(false); };

  const stackLayers = [
    { c: G.green, sx: "9px", sy: "10px", sr: "-9deg", op: 0.55 },
    { c: G.yellow, sx: "6px", sy: "6px", sr: "5deg", op: 0.7 },
    { c: G.red, sx: "3px", sy: "3px", sr: "-3deg", op: 0.85 },
  ];

  return (
    <div
      onMouseMove={handleMove}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={reset}
      style={{ position: "relative", width: 104, height: 104, perspective: 700, cursor: "pointer" }}
    >
      {stackLayers.map((s, i) => (
        <div
          key={i}
          style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            background: s.c, opacity: s.op,
            // @ts-ignore — custom props for the keyframe
            "--sx": s.sx, "--sy": s.sy, "--sr": s.sr,
            transform: `translate(${s.sx},${s.sy}) rotate(${s.sr})`,
            animation: `stackWiggle ${4.5 + i}s ease-in-out infinite`,
          }}
        />
      ))}

      <div
        style={{
          position: "relative", width: 104, height: 104, borderRadius: "50%",
          background: G.white,
          boxShadow: `0 0 0 4px ${G.white}, 0 0 0 7px ${G.blue}, 0 9px 0 ${G.blueDeep}, 0 20px 30px -10px rgba(32,33,36,.35)`,
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
          transformStyle: "preserve-3d",
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${hovering ? 1.06 : 1})`,
          transition: "transform .18s cubic-bezier(.2,.9,.3,1.2)",
          animation: "logoPop .7s cubic-bezier(.34,1.56,.64,1) both, floatY 4.5s ease-in-out .7s infinite",
        }}
      >
        <img
          src={logoImg}
          alt="App logo"
          style={{ width: "68%", height: "68%", objectFit: "contain", position: "relative", zIndex: 1 }}
        />
        {/* glossy diagonal shine sweep */}
        <div
          style={{
            position: "absolute", top: -20, left: -40, width: "60%", height: "160%",
            background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,.85) 48%, transparent 62%)",
            animation: "shine 3.4s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   AUTH SWITCH
══════════════════════════════════════════════════════════ */
const AuthSwitch: React.FC = () => {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, user, authLoaded } = useSelector((s: RootState) => s.auth);

  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);
  useEffect(() => { if (authLoaded && user) navigate("/"); }, [authLoaded, user, navigate]);

  const toggle = (m: "login" | "signup") => {
    setMode(m); setError(""); setPassword(""); setConfirmPassword("");
  };

  const check = validatePassword(password, email);
  const allValid = Object.values(check).every(Boolean);
  const score = [check.hasUpper, check.hasLower, check.hasNumber, check.hasSpecial, check.validLength].filter(Boolean).length;
  const strength = ["Too Weak", "Weak", "Good", "Strong", "Very Strong"][score] ?? "Too Weak";
  const strengthColor = score <= 1 ? G.red : score <= 2 ? G.yellow : score <= 3 ? G.blue : G.green;

  const handleLogin = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    try { await dispatch(loginWithEmail({ email, password })).unwrap(); navigate("/"); }
    catch (e: any) { setError(e.message || "Login failed"); }
  };

  const handleRegister = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    if (password !== confirmPassword) return setError("Passwords do not match");
    if (!allValid) return setError("Password must meet all requirements");
    try {
      const [firstName = "", lastName = ""] = email.split("@")[0].split(".");
      await dispatch(registerWithEmail({ email, password, firstName, lastName, phoneNumber: "", profilePicture: "", username: "" })).unwrap();
      navigate("/");
    } catch (e: any) { setError(e.message || "Something went wrong"); }
  };

  const handleGoogle = async () => {
    try { await dispatch(loginWithGoogle()).unwrap(); navigate("/"); }
    catch (e: any) { setError(e.message || "Google login failed"); }
  };

  return (
    <div style={{
      minHeight: "100vh", background: G.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: "'Nunito', sans-serif",
      position: "relative", overflow: "hidden",
    }}>
      <style>{FONT}</style>

      {/* Background orbs */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, #4285F420, transparent 70%)", animation: "orb 10s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, #34A85318, transparent 70%)", animation: "orb 13s ease-in-out infinite reverse" }} />
        <div style={{ position: "absolute", top: "40%", left: "20%", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, #EA433512, transparent 70%)", animation: "orb 8s ease-in-out infinite 2s" }} />
      </div>

      {/* Card — thick white bevel, no flat edges */}
      <div className="auth-card" style={{
        width: "100%", maxWidth: 460, position: "relative", zIndex: 1,
        background: G.white, borderRadius: 30,
        boxShadow: `0 0 0 4px ${G.white}, 0 0 0 8px ${G.border}, 0 16px 0 ${G.faint}, 0 44px 70px -30px rgba(32,33,36,.35)`,
        overflow: "hidden",
      }}>
        <div style={{ padding: "38px 34px 34px" }}>

          {/* ── Logo only — no wordmark, no product name ── */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 26 }}>
            <LogoBadge />
          </div>

          {/* ── Toggle — pill with a lifted 3D thumb ── */}
          <div style={{
            position: "relative", display: "flex", background: G.bg,
            borderRadius: 999, padding: 5, marginBottom: 26,
            boxShadow: `inset 0 0 0 2.5px ${G.border}`,
          }}>
            <div style={{
              position: "absolute", top: 5, bottom: 5,
              width: "calc(50% - 5px)",
              left: mode === "login" ? 5 : "calc(50% + 0px)",
              borderRadius: 999, background: G.blue,
              boxShadow: `0 0 0 2px ${G.white}, 0 4px 0 ${G.blueDeep}`,
              transition: "left .28s cubic-bezier(.2,.9,.3,1.2)",
            }} />
            {(["login", "signup"] as const).map(m => (
              <button
                key={m}
                onClick={() => toggle(m)}
                style={{
                  position: "relative", zIndex: 1, flex: 1, padding: "11px 0",
                  borderRadius: 999, border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 800, letterSpacing: "0.06em",
                  fontFamily: "'Baloo 2', sans-serif", background: "transparent",
                  color: mode === m ? G.white : G.soft,
                  transition: "color .2s",
                }}
              >
                {m === "login" ? "LOGIN" : "SIGN UP"}
              </button>
            ))}
          </div>

          {/* ── Title ── */}
          <h2 style={{
            fontSize: 21, fontWeight: 800, color: G.text,
            marginBottom: 22, fontFamily: "'Baloo 2', sans-serif",
          }}>
            {mode === "login" ? "Welcome back 👋" : "Create your account"}
          </h2>

          {/* ── Fields ── */}
          <Field label="Email Address" value={email} onChange={setEmail} type="email" />

          <Field
            label="Password"
            value={password}
            onChange={setPassword}
            type={showPassword ? "text" : "password"}
            error={mode === "signup" && password.length > 0 && !allValid}
            right={
              <div onClick={() => setShowPassword(p => !p)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
            }
          />

          {/* Strength bar — signup only */}
          {mode === "signup" && password.length > 0 && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 5, marginBottom: 7 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} style={{
                    flex: 1, height: 6, borderRadius: 999,
                    background: i <= score ? strengthColor : G.border,
                    transition: "background 0.3s",
                  }} />
                ))}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: strengthColor, fontFamily: "'Baloo 2', sans-serif" }}>
                {strength}
              </span>
            </div>
          )}

          {/* Password checklist — signup only */}
          {mode === "signup" && password.length > 0 && (
            <div style={{
              marginBottom: 16, padding: "14px 16px",
              background: G.bg, borderRadius: 18,
              boxShadow: `inset 0 0 0 2.5px ${G.border}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G.soft, letterSpacing: "0.12em", marginBottom: 10, fontFamily: "'Baloo 2', sans-serif" }}>
                REQUIREMENTS
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px 12px" }}>
                {[
                  { ok: check.hasUpper, text: "Uppercase letter" },
                  { ok: check.hasLower, text: "Lowercase letter" },
                  { ok: check.hasNumber, text: "Number" },
                  { ok: check.hasSpecial, text: "Special character" },
                  { ok: check.validLength, text: "6–12 characters" },
                  { ok: check.notContainEmail, text: "Not your email" },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 7,
                    fontSize: 11, fontWeight: 700,
                    color: item.ok ? G.greenDeep : G.soft,
                    fontFamily: "'Nunito', sans-serif",
                  }}>
                    <div style={{
                      width: 17, height: 17, borderRadius: "50%", flexShrink: 0,
                      background: item.ok ? G.green : G.white,
                      boxShadow: item.ok ? `0 0 0 2px ${G.greenDeep}` : `0 0 0 2px ${G.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background .18s, box-shadow .18s",
                    }}>
                      {item.ok
                        ? <Check size={9} color="#fff" strokeWidth={3.4} />
                        : <X size={9} color={G.faint} strokeWidth={3.4} />}
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === "signup" && (
            <Field
              label="Confirm Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              type="password"
              error={confirmPassword.length > 0 && confirmPassword !== password}
            />
          )}

          {/* Error */}
          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: "11px 14px", borderRadius: 16, marginBottom: 16,
              background: "#FDECEA", boxShadow: `inset 0 0 0 2px ${G.red}`,
              fontSize: 12.5, color: G.redDeep, fontWeight: 700,
              fontFamily: "'Nunito', sans-serif",
            }}>
              {error}
            </div>
          )}

          {/* ── Main CTA — the one button that's always this blue ── */}
          <button
            className="eb-btn"
            onClick={mode === "login" ? handleLogin : handleRegister}
            disabled={status === "loading"}
            style={{
              width: "100%", padding: "15px",
              background: status === "loading" ? G.faint : `linear-gradient(168deg, ${G.blueLit} 0%, ${G.blue} 60%, ${G.blueDeep} 100%)`,
              color: G.white, border: "none", borderRadius: 999,
              fontSize: 14.5, fontWeight: 800,
              cursor: status === "loading" ? "not-allowed" : "pointer",
              fontFamily: "'Baloo 2', sans-serif", letterSpacing: "0.03em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: status === "loading" ? "none" : `0 0 0 2px ${G.white}, 0 0 0 4.5px ${G.blueDeep}, 0 7px 0 ${G.blueDeep}`,
              marginBottom: 18,
            }}
          >
            {status === "loading"
              ? (mode === "login" ? "Signing in…" : "Creating account…")
              : (mode === "login" ? "Sign In" : "Create Account")}
            {status !== "loading" && <ArrowRight size={17} strokeWidth={2.8} />}
          </button>

          {/* ── Divider ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 2, background: G.border, borderRadius: 2 }} />
            <span style={{ fontSize: 10.5, color: G.faint, fontWeight: 800, letterSpacing: "0.16em", fontFamily: "'Baloo 2', sans-serif" }}>OR</span>
            <div style={{ flex: 1, height: 2, background: G.border, borderRadius: 2 }} />
          </div>

          {/* ── Google ── */}
          <button
            className="eb-btn"
            onClick={handleGoogle}
            style={{
              width: "100%", padding: "13px",
              background: G.bg,
              boxShadow: `inset 0 0 0 2.5px ${G.border}`,
              borderRadius: 999, border: "none",
              fontSize: 14, fontWeight: 800, cursor: "pointer",
              fontFamily: "'Baloo 2', sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              color: G.text, marginBottom: 22,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          {/* ── Footer links ── */}
          {mode === "login" ? (
            <div style={{ textAlign: "center" }}>
              <span
                onClick={() => navigate("/ResetPassword")}
                style={{
                  fontSize: 13, color: G.soft, cursor: "pointer",
                  fontWeight: 700, fontFamily: "'Nunito', sans-serif",
                  boxShadow: `inset 0 -2px 0 ${G.border}`, paddingBottom: 2,
                }}
              >
                Forgot password?
              </span>
            </div>
          ) : (
            <p style={{ textAlign: "center", fontSize: 13, color: G.soft, fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}>
              Already have an account?{" "}
              <span onClick={() => toggle("login")} style={{ color: G.blue, cursor: "pointer", fontWeight: 800 }}>
                Sign In
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthSwitch;